import type { JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { MerchantMultiSelect } from '../design-system/merchant-picker.tsx'
import { Input } from '../design-system/input.tsx'
import { Notice } from '../design-system/notice.tsx'
import { Select, type SelectOption } from '../design-system/select.tsx'
import {
  ALERT_DETAILS,
  ALERT_FIGURE_OPS,
  type AlertReport
} from '../lib/alert-rules-api.ts'
import {
  DELIVERY_CHOICES,
  DELIVERY_LABEL,
  DETAIL_HINT,
  DETAIL_LABEL,
  FIGURE_OP_LABEL,
  LABEL_MAX_LENGTH,
  TRIGGER_LABEL,
  choiceEmails,
  choicePostsToChannel,
  figureValueProblem,
  formatAlertHour,
  labelProblem,
  merchantRowsProblem,
  type AlertDraft
} from '../lib/alert-draft.ts'
import { alertChips } from '../lib/alert-chips.ts'
import { AlertFilterAdd } from './alert-filter-add.tsx'
import { EmailToField } from './email-to-field.tsx'
import { SlackChannelField } from './slack-channel-field.tsx'
import { searchText, type AlertSearch } from '../lib/alert-view.ts'
import type { Merchant } from '../lib/merchants.ts'
import { formatCount } from '../lib/format.ts'
import { PERIOD_PRESETS } from '../lib/period-presets.ts'
import { WEEK_START_LABELS, WEEK_STARTS } from '../lib/reporting-period.ts'

/** Sets one field of the draft. */
export type SetField = <K extends keyof AlertDraft>(
  key: K,
  value: AlertDraft[K]
) => void

/** What each step reads and writes. */
interface StepProps {
  report: AlertReport
  draft: AlertDraft
  setField: SetField
}

const OPERATORS: readonly SelectOption[] = ALERT_FIGURE_OPS.map(op => ({
  value: op,
  label: FIGURE_OP_LABEL[op]
}))

const DETAILS: readonly SelectOption[] = ALERT_DETAILS.map(detail => ({
  value: detail,
  label: DETAIL_LABEL[detail]
}))

const DELIVERIES: readonly SelectOption[] = DELIVERY_CHOICES.map(choice => ({
  value: choice,
  label: DELIVERY_LABEL[choice]
}))

const HOURS: readonly SelectOption[] = Array.from(
  { length: 24 },
  (_, hour) => ({
    value: String(hour),
    label: formatAlertHour(hour)
  })
)

const WEEKDAYS: readonly SelectOption[] = WEEK_STARTS.map(day => ({
  value: String(day),
  label: WEEK_START_LABELS[day]
}))

/**
 * Step 1: the period, the merchants, and the view's filters: chips the reader
 * can remove, and a control that adds any filter the report's table takes.
 *
 * What the reader sets here sits over the page's own URL, in `draft.added`.
 * Removing a chip drops it from both.
 *
 * @param props - Step props.
 * @param props.report - The registry entry.
 * @param props.draft - The sheet's choices.
 * @param props.setField - Sets one choice.
 * @param props.search - The page's URL search.
 * @param props.merchants - Names for the merchant ids.
 * @returns The step.
 */
export function WatchStep({
  report,
  draft,
  setField,
  search,
  merchants
}: StepProps & {
  search: AlertSearch
  merchants: readonly Merchant[]
}): JSX.Element {
  const periodOptions = report.periods.flatMap(id => {
    const preset = PERIOD_PRESETS.find(item => item.id === id)

    return preset && preset.id !== 'custom'
      ? [{ value: preset.id, label: preset.label }]
      : []
  })
  const chips = alertChips(
    search,
    report.params,
    merchants,
    draft.dropped,
    report.words
  )
  const set = (param: string, value: string): void => {
    setField('added', { ...draft.added, [param]: value })
    setField(
      'dropped',
      draft.dropped.filter(item => item !== param)
    )
  }
  const drop = (param: string): void => {
    const { [param]: _gone, ...added } = draft.added
    setField('added', added)
    setField('dropped', [...draft.dropped, param])
  }
  const merchantParam = report.words.scope?.param
  const picksMerchants =
    merchantParam === 'orgIds' && report.params.scope.includes(merchantParam)
  const pickedIds = draft.dropped.includes('orgIds')
    ? []
    : (searchText(search.orgIds) ?? '')
        .split(',')
        .map(Number)
        .filter(id => Number.isInteger(id) && id > 0)

  return (
    <>
      {report.takesPeriod ? (
        <>
          <Select
            data-testid="alert-sheet-period"
            label="Period"
            placeholder="Choose a period"
            options={periodOptions}
            value={draft.period}
            onChange={event => setField('period', event.target.value)}
          />
          {draft.period === '' ? (
            <Notice data-testid="alert-sheet-period-notice">
              A saved alert needs a named period.
            </Notice>
          ) : null}
        </>
      ) : null}
      {chips.length > 0 ? (
        <ul
          data-testid="alert-sheet-chips"
          className="flex list-none flex-wrap gap-2 p-0"
        >
          {chips.map(chip => (
            <li key={chip.param}>
              <Button
                data-testid={`alert-sheet-chip-${chip.slug}-remove`}
                variant="outline"
                onClick={() => drop(chip.param)}
              >
                {chip.label}
                <span className="sr-only">Remove</span>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p
          data-testid="alert-sheet-no-filters"
          className="text-sm text-muted-foreground"
        >
          No scope or column filters on this view.
        </p>
      )}
      {picksMerchants ? (
        <MerchantMultiSelect
          data-testid="alert-sheet-merchants"
          label="Merchants"
          placeholder="Every merchant"
          merchants={merchants}
          value={pickedIds}
          onChange={ids => set('orgIds', ids.join(','))}
        />
      ) : null}
      <AlertFilterAdd
        fields={report.params.fields ?? []}
        words={report.words}
        onAdd={set}
      />
    </>
  )
}

/**
 * Step 2: the trigger, and the figure comparison when the trigger is a figure,
 * or the row count when it is per merchant.
 *
 * The registry offers `figure` only on a report with figures, and
 * `merchantRows` only on a report whose rows name their merchant, and
 * `merchantNewRows` only when those rows also have a key.
 *
 * @param props - Step props.
 * @param props.report - The registry entry.
 * @param props.draft - The sheet's choices.
 * @param props.setField - Sets one choice.
 * @returns The step.
 */
export function TriggerStep({
  report,
  draft,
  setField
}: StepProps): JSX.Element {
  const valueProblem = figureValueProblem(draft.op, draft.value)

  return (
    <>
      <Select
        data-testid="alert-sheet-trigger"
        label="Tell me"
        options={report.triggers.map(kind => ({
          value: kind,
          label: TRIGGER_LABEL[kind]
        }))}
        value={draft.trigger}
        onChange={event => setField('trigger', event.target.value)}
      />
      {draft.trigger === 'figure' ? (
        <>
          <Select
            data-testid="alert-sheet-figure"
            label="Figure"
            options={report.figures.map(item => ({
              value: item.id,
              label: item.label
            }))}
            value={draft.figure}
            onChange={event => setField('figure', event.target.value)}
          />
          <Select
            data-testid="alert-sheet-operator"
            label="Operator"
            options={
              !report.takesPeriod
                ? OPERATORS.filter(item => item.value !== 'changePct')
                : OPERATORS
            }
            value={draft.op}
            onChange={event => setField('op', event.target.value)}
          />
          <Input
            data-testid="alert-sheet-value"
            label="Value"
            value={draft.value}
            onChange={event => setField('value', event.target.value)}
            inputMode="decimal"
            error={
              draft.value.trim() === ''
                ? undefined
                : (valueProblem ?? undefined)
            }
          />
        </>
      ) : null}
      {draft.trigger === 'merchantRows' ||
      draft.trigger === 'merchantNewRows' ? (
        <Input
          data-testid="alert-sheet-merchant-rows"
          label={
            draft.trigger === 'merchantNewRows'
              ? 'New rows for any one merchant'
              : 'Rows for any one merchant'
          }
          value={draft.value}
          onChange={event => setField('value', event.target.value)}
          inputMode="numeric"
          error={
            draft.value.trim() === ''
              ? undefined
              : (merchantRowsProblem(draft.value) ?? undefined)
          }
        />
      ) : null}
    </>
  )
}

/**
 * Step 3: how often the alert is checked, where it goes, how much it carries, and a label.
 *
 * An email goes to the addresses the reader names, or to their own when they name none.
 *
 * @param props - Step props.
 * @param props.report - The registry entry.
 * @param props.draft - The sheet's choices.
 * @param props.setField - Sets one choice.
 * @returns The step.
 */
export function ScheduleStep({
  report,
  draft,
  setField
}: StepProps): JSX.Element {
  const scheduleOptions = [
    ...(report.hourly
      ? [
          { value: 'quarterHour', label: 'Every 15 minutes' },
          { value: 'hour', label: 'Every hour' }
        ]
      : []),
    { value: 'day', label: 'Every day' },
    { value: 'week', label: 'Every week' }
  ]

  return (
    <>
      <Select
        data-testid="alert-sheet-schedule"
        label="How often"
        options={scheduleOptions}
        value={draft.every}
        onChange={event => setField('every', event.target.value)}
      />
      {draft.every === 'hour' || draft.every === 'quarterHour' ? null : (
        <Select
          data-testid="alert-sheet-hour"
          label="Hour"
          hint="In your time zone."
          options={HOURS}
          value={draft.hour}
          onChange={event => setField('hour', event.target.value)}
        />
      )}
      {draft.every === 'week' ? (
        <Select
          data-testid="alert-sheet-weekday"
          label="Day"
          options={WEEKDAYS}
          value={draft.weekday}
          onChange={event => setField('weekday', event.target.value)}
        />
      ) : null}
      <Select
        data-testid="alert-sheet-delivery"
        label="Send to"
        options={DELIVERIES}
        value={draft.delivery}
        onChange={event => setField('delivery', event.target.value)}
      />
      {choicePostsToChannel(draft.delivery) ? (
        <SlackChannelField
          data-testid="alert-sheet-slack-channel"
          value={draft.slackChannel}
          onChange={value => setField('slackChannel', value)}
        />
      ) : null}
      {choiceEmails(draft.delivery) ? (
        <EmailToField
          data-testid="alert-sheet-email-to"
          value={draft.emailTo}
          onChange={value => setField('emailTo', value)}
        />
      ) : null}
      <Select
        data-testid="alert-sheet-detail"
        label="What the alert shows"
        hint={DETAIL_HINT[draft.detail]}
        options={DETAILS}
        value={draft.detail}
        onChange={event => setField('detail', event.target.value)}
      />
      <Input
        data-testid="alert-sheet-label"
        label="Label (optional)"
        value={draft.label}
        onChange={event => setField('label', event.target.value)}
        error={labelProblem(draft.label) ?? undefined}
        hint={`At most ${formatCount(LABEL_MAX_LENGTH)} characters. Blank saves no label.`}
      />
    </>
  )
}
